import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import TextInput from '@components/dataEntry/Input/TextInput';

const RangeWrap = styled.div`
  display: flex;
  gap: 8px;
`;

const shapeMap = {
  sm: css`
    height: 6px;
    &::-webkit-slider-thumb {
      width: 12px;
      height: 12px;
    }
  `,
  md: css`
    height: 12px;
    &::-webkit-slider-thumb {
      width: 20px;
      height: 20px;
    }
  `,
};

const Slider = styled.input<{
  shape: keyof typeof shapeMap;
  primaryColor: string;
}>`
  appearance: none; // 기본스타일 숨기기
  width: 100%;
  border-radius: 50px;
  outline: none;
  margin: 12px 0;
  border: 1px solid var(--lineColor);
  transition: opacity 0.2s;
  ${({ shape }) => shape && shapeMap[shape]};

  &::-webkit-slider-thumb {
    appearance: none; // 기본스타일 숨기기
    -webkit-appearance: none;
    border-radius: 50px;
    background-color: ${({ primaryColor }) => primaryColor};
    cursor: var(--hoverCursorURL), pointer;
    transition: border-color 0.3s, box-shadow 0.6s,
      transform 0.3s cubic-bezier(0.18, 0.89, 0.32, 1.28);
    transform-origin: center center;

    &:hover {
      transform: scale(1.2);
      box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.1);
    }
  }
`;

const getTrackBackground = ({ value, min, max, primaryColor, bgColor }) => {
  const percent = ((value - min) / (max - min)) * 100;
  return `linear-gradient(to right, ${primaryColor} 0%, ${primaryColor} ${percent}%, ${bgColor} ${percent}%, ${bgColor} 100%)`;
};

type SliderProps = {
  value: number;
  onChange?: any;
  disabled?: boolean;
  range?: any;
  min?: number;
  max?: number;
  step?: number;
  id?: string;
  name?: string;
  customChange?: (any) => void;
  inputWidth?: number;
  showInput?: boolean;
  primaryColor?: string;
  bgColor?: string;
  shape?: keyof typeof shapeMap;
};

function RangeSlider({
  value,
  onChange,
  customChange,
  step,
  min,
  max = 10,
  disabled,
  id,
  name,
  inputWidth,
  showInput = true,
  primaryColor,
  bgColor,
  shape = 'md',
}: SliderProps) {
  const handleValue = useCallback(
    (e) => {
      if (onChange) {
        onChange(e.target.value);
      }
    },
    [onChange],
  );

  return (
    <RangeWrap>
      <Slider
        shape={shape || 'md'}
        primaryColor={primaryColor || 'var(--primaryColor)'}
        type="range"
        min={min || 0}
        max={max}
        value={value || 0}
        step={step}
        onChange={customChange || handleValue}
        style={{
          background: getTrackBackground({
            value,
            min: min || 0,
            max,
            primaryColor: primaryColor || 'var(--primaryColor)',
            bgColor: bgColor || 'var(--cardBackgroundColor',
          }),
        }}
      />
      {showInput && (
        <TextInput
          name={name}
          id={id}
          type="number"
          min={min || 0}
          max={max}
          value={value || 0}
          onChange={customChange || handleValue}
          width={inputWidth || 80}
        />
      )}
    </RangeWrap>
  );
}

export default RangeSlider;
